import React, { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Typography, Avatar, Tag, ButtonMenu } from "@ann/web-components";
import { NameInitialsAvatar } from "react-name-initials-avatar";
import {
  getAvatarUrl,
  generateAvatarColor,
} from "../../../../../../../utils/avatarUtils";
import {
  CardWrapper,
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
  DepartmentName,
  DepartmentDescription,
  StatisticsSection,
  StatItem,
  Divider,
  DepartmentAdminSection,
  AdminInfo,
  KnowledgeBasesSection,
  KnowledgeBasesTags,
  DepartmentAvatarWithInitials,
} from "./DepartmentCard.styled";
import DepartmentDetailSlideout from "../DepartmentDetailSlideout";

const DepartmentCard = ({ department, onClick }) => {
  const navigate = useNavigate();
  const [imageFailed, setImageFailed] = useState(false);
  const [isSlideoutOpen, setIsSlideoutOpen] = useState(false);

  const departmentId = useMemo(() => {
    return (
      department.departmentUuid ||
      department.department_uuid ||
      department.department_id ||
      department.id ||
      department.name
    );
  }, [
      department.departmentUuid,
      department.department_uuid,
      department.department_id,
      department.id,
      department.name,
    ]);

  const departmentName =
    department.departmentName || department.department_name || department.name || "Unnamed Department";
  const ownerName = department.ownerName || department.owner_name || "";
  const userCount = department.userCount ?? department.user_count ?? 0;
  const groupsCount = department.groupsCount ?? department.groups_count ?? 0;
  const description =
    department.description ||
    department.department_description ||
    "No description available.";
  const photoUrl = department.profilePhotoUrl || department.photo_url;

  const knowledgeBases = useMemo(
    () => department.knowledge_bases || department.knowledgeBases || [],
    [department.knowledge_bases, department.knowledgeBases]
  );

  const avatarUrl = imageFailed ? null : getAvatarUrl(photoUrl, ownerName);
  const avatarColor = generateAvatarColor(ownerName);
  const showInitialsAvatar = imageFailed || !avatarUrl;

  const handleImageError = useCallback(() => setImageFailed(true), []);
  const handleOpenSlideout = useCallback(() => setIsSlideoutOpen(true), []);
  const handleCloseSlideout = useCallback(() => setIsSlideoutOpen(false), []);

  const handleMenuChange = useCallback((value) => {
    if (value === "view_details") handleOpenSlideout();
  }, [handleOpenSlideout]);

  const getDepartmentData = useCallback(
    () => ({
      department_id: department.department_id,
      departmentUuid: department.departmentUuid || department.department_uuid,
      department_uuid: department.department_uuid || department.departmentUuid,
      id: department.id,
      departmentName: department.departmentName || department.department_name,
      department_name: department.department_name || department.departmentName,
      name: department.name,
      status: department.status,
      photo_url: department.photo_url,
      profilePhotoUrl: department.profilePhotoUrl,
      createdAt: department.createdAt || department.created_at,
      created_at: department.created_at || department.createdAt,
      user_count: department.user_count,
      userCount: department.userCount,
      groups_count: department.groups_count,
      groupsCount: department.groupsCount,
      ownerName: department.ownerName || department.owner_name,
      owner_name: department.owner_name || department.ownerName,
      description: department.description,
      admin_role: department.admin_role,
      knowledge_bases: Array.isArray(knowledgeBases) ? [...knowledgeBases] : [],
    }),
    [department, knowledgeBases]
  );

  const handleCardClick = useCallback(() => {
    if (onClick) {
      onClick(departmentId);
      return;
    }

    if (!departmentId) {
      return;
    }

    navigate(`/v2/departments/${departmentId}/overview`, {
      state: {
        departmentName,
        departmentData: getDepartmentData(),
      },
    });
  }, [
    onClick,
    departmentId,
    navigate,
    departmentName,
    getDepartmentData,
  ]);

  const visibleKnowledgeBases = useMemo(
    () => knowledgeBases.slice(0, 2),
    [knowledgeBases]
  );
  const remainingCount = useMemo(
    () => Math.max(0, knowledgeBases.length - 2),
    [knowledgeBases]
  );

  return (
    <>
      <Card onClick={handleCardClick}>
        <CardWrapper>
        <CardHeader>
          <CardHeaderLeft />
          <CardHeaderRight>
            <ButtonMenu
              customIcon="dotsMenu"
              disableEffects
              iconPosition="right"
              label=""
              menuPosition="bottom"
              alignMenu="right"
              onChange={handleMenuChange}
              onClick={(e) => e.stopPropagation()}
              size="medium"
              type="tertiary"
              width="auto"
            >
              <ButtonMenu.Menu icon={true}>
                <ButtonMenu.Item
                  label="View details"
                  value="view_details"
                  icon=""
                />
              </ButtonMenu.Menu>
            </ButtonMenu>
          </CardHeaderRight>
        </CardHeader>

        <DepartmentName>
          <Typography
            category="title"
            size="large"
            weight="bold"
            maxCharacters={20}
            titleOnHover={true}
          >
            {departmentName}
          </Typography>
        </DepartmentName>

        <DepartmentDescription>
          <Typography
            category="body"
            size="medium"
            weight="regular"
            color="body"
            maxCharacters={40}
            titleOnHover={true}
          >
            {description}
          </Typography>
        </DepartmentDescription>

        <StatisticsSection>
          <StatItem>
            <Typography
              category="body"
              size="small"
              weight="regular"
              color="body"
            >
              Users
            </Typography>
            <Typography category="title" size="small" weight="bold">
              {userCount}
            </Typography>
          </StatItem>
          <StatItem>
            <Typography
              category="body"
              size="small"
              weight="regular"
              color="body"
            >
              Groups
            </Typography>
            <Typography category="title" size="small" weight="bold">
              {groupsCount}
            </Typography>
          </StatItem>
        </StatisticsSection>

        <Divider />

        <DepartmentAdminSection>
          {!showInitialsAvatar && avatarUrl ? (
            <Avatar
              alt={ownerName}
              size="md"
              src={avatarUrl}
              onError={handleImageError}
            />
          ) : (
            <DepartmentAvatarWithInitials>
              <NameInitialsAvatar
                name={ownerName || "Unknown"}
                bgColor={avatarColor}
                textColor="#ffffff"
                size="40px"
                borderRadius="50%"
                textWeight="bold"
                textSize="14px"
                borderWidth="0"
              />
            </DepartmentAvatarWithInitials>
          )}
          <AdminInfo>
            <Typography category="body" size="medium" weight="regular">
              {ownerName || "Unknown Admin"}
            </Typography>
          </AdminInfo>
        </DepartmentAdminSection>

        {knowledgeBases.length > 0 && (
          <KnowledgeBasesSection>
            <Typography
              category="body"
              size="small"
              weight="regular"
              color="body"
            >
              Knowledge Bases:
            </Typography>
            <KnowledgeBasesTags>
              {visibleKnowledgeBases.map((kb, index) => (
                <Tag key={index} color="neutral" size="small" text={kb} />
              ))}
              {remainingCount > 0 && (
                <Tag color="neutral" size="small" text={`+${remainingCount}`} />
              )}
            </KnowledgeBasesTags>
          </KnowledgeBasesSection>
        )}
        </CardWrapper>
      </Card>

      <DepartmentDetailSlideout
        open={isSlideoutOpen}
        onClose={handleCloseSlideout}
        department={getDepartmentData()}
      />
    </>
  );
};

export default DepartmentCard;
